import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { datos } from '@/api/origen'
import { descargarArchivo } from '@/api/cliente'
import { ErrorApi } from '@/api/errores'
import type { ReporteContable } from '@/dominio/contabilidad/tipos'
import type {
  AplicacionCartera,
  ClienteCartera,
  ConfiguracionCartera,
  EstadoCartera,
  EstadoRecordatorio,
  EvaluacionCredito,
  FacturaCartera,
  FichaCliente,
  HallazgoValidacion,
  PeticionAplicarSaldo,
  PeticionClienteCartera,
  PeticionConfiguracionCartera,
  PeticionPlantillaRecordatorio,
  PeticionRecibo,
  PeticionRecordatorio,
  PlantillasRecordatorio,
  ReciboCartera,
  ReciboDetalle,
  ReciboRegistrado,
  Recordatorio,
  RecordatorioPedido,
  ResumenCartera,
  SaldosAFavor,
  WhatsAppDocumento,
} from './tipos'

/**
 * Los datos de las cuentas por cobrar. Todo va contra
 * `/interna/cuentas-por-cobrar`, que exige el permiso del rol en cada ruta: la
 * pantalla esconde lo que el rol no puede hacer por comodidad, y el servidor lo
 * vuelve a negar por seguridad.
 *
 * **Las dos escrituras que mueven dinero llevan clave de idempotencia**: el
 * recibo y la aplicación de un saldo a favor. Las genera el formulario una vez
 * por instancia (`useClaveIdempotencia`) y viajan en `Idempotency-Key`; con ella
 * el transporte reintenta un corte de red sin cobrar dos veces. Las anulaciones
 * no la necesitan: anular lo ya anulado es un 409, no un segundo contramovimiento.
 */

const BASE = '/cuentas-por-cobrar'

export const clavesCartera = {
  todo: ['cartera'] as const,
  estado: ['cartera', 'estado'] as const,
  configuracion: ['cartera', 'configuracion'] as const,
  resumen: ['cartera', 'resumen'] as const,
  antiguedad: ['cartera', 'antiguedad'] as const,
  clientes: (texto: string, todos: boolean) => ['cartera', 'clientes', texto, todos] as const,
  ficha: (id: string) => ['cartera', 'ficha', id] as const,
  estadoDeCuenta: (id: string, desde: string, hasta: string) => ['cartera', 'estado-de-cuenta', id, desde, hasta] as const,
  facturas: (cliente: string | null, todas: boolean) => ['cartera', 'facturas', cliente ?? '', todas] as const,
  recibos: (f: FiltroRecibos) => ['cartera', 'recibos', f] as const,
  recibo: (id: string) => ['cartera', 'recibo', id] as const,
  saldosAFavor: (cliente: string | null) => ['cartera', 'saldos-a-favor', cliente ?? ''] as const,
  recordatorios: (estado: EstadoRecordatorio | null) => ['cartera', 'recordatorios', estado ?? ''] as const,
  plantillas: ['cartera', 'plantillas'] as const,
  credito: (identificacion: string, monto: string) => ['cartera', 'credito', identificacion, monto] as const,
}

export interface FiltroRecibos {
  desde?: string
  hasta?: string
  cliente?: string
  texto?: string
}

/** Lo que mueve un cobro cambia el resumen, la ficha, los recibos y la contabilidad: se invalida la cartera entera. */
function invalidar(cliente: QueryClient): Promise<unknown> {
  return cliente.invalidateQueries({ queryKey: clavesCartera.todo })
}

/**
 * Qué puede hacer el rol. Con un error (red, sesión) se da por sin acceso: el
 * menú no enseña una entrada que luego contesta 403.
 */
export function useEstadoCartera() {
  return useQuery({
    queryKey: clavesCartera.estado,
    queryFn: async ({ signal }) => {
      try {
        return await datos.obtener<EstadoCartera>(`${BASE}/estado`, { senal: signal })
      } catch {
        return {
          puedeVer: false,
          puedeCobrar: false,
          puedeConfigurar: false,
          puedeRecurrentes: false,
          cajaConectada: false,
          correoConfigurado: false,
        } satisfies EstadoCartera
      }
    },
    staleTime: 300_000,
  })
}

export function useConfiguracionCartera() {
  return useQuery({
    queryKey: clavesCartera.configuracion,
    queryFn: ({ signal }) => datos.obtener<ConfiguracionCartera>(`${BASE}/configuracion`, { senal: signal }),
  })
}

export function useGuardarConfiguracionCartera() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (v: PeticionConfiguracionCartera) => datos.reemplazar<ConfiguracionCartera>(`${BASE}/configuracion`, { cuerpo: v }),
    onSuccess: () => invalidar(cliente),
  })
}

/** «Traer lo emitido»: el barrido de un año para lo que entró antes de la cartera o se le escapó a un disparador. */
export function useSincronizarCartera() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: () => datos.crear<{ nuevos: number }>(`${BASE}/sincronizar`),
    onSuccess: () => invalidar(cliente),
  })
}

export function useResumenCartera() {
  return useQuery({
    queryKey: clavesCartera.resumen,
    queryFn: ({ signal }) => datos.obtener<ResumenCartera>(`${BASE}/resumen`, { senal: signal }),
    // Los cobros los registran varias personas y la caja vende a crédito todo el día.
    refetchInterval: 60_000,
  })
}

export function useAntiguedad() {
  return useQuery({
    queryKey: clavesCartera.antiguedad,
    queryFn: ({ signal }) => datos.obtener<ReporteContable>(`${BASE}/antiguedad`, { senal: signal }),
  })
}

export function descargarAntiguedad(formato: 'pdf' | 'xlsx' | 'csv'): Promise<void> {
  return descargarArchivo(`${BASE}/antiguedad`, { formato })
}

export function useClientesCartera(texto: string, todos: boolean) {
  const limpio = texto.trim()
  return useQuery({
    queryKey: clavesCartera.clientes(limpio, todos),
    queryFn: ({ signal }) =>
      datos.obtener<ClienteCartera[]>(`${BASE}/clientes`, { consulta: { texto: limpio, todos: todos || undefined }, senal: signal }),
    placeholderData: (anterior) => anterior,
  })
}

export function useFichaCliente(id: string | null) {
  return useQuery({
    queryKey: clavesCartera.ficha(id ?? ''),
    queryFn: ({ signal }) => datos.obtener<FichaCliente>(`${BASE}/clientes/${encodeURIComponent(id ?? '')}`, { senal: signal }),
    enabled: id !== null,
  })
}

export function useGuardarClienteCartera() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...v }: PeticionClienteCartera & { id: string }) =>
      datos.reemplazar<void>(`${BASE}/clientes/${encodeURIComponent(id)}`, { cuerpo: v }),
    onSuccess: () => invalidar(cliente),
  })
}

export function useEstadoDeCuenta(id: string | null, desde: string, hasta: string) {
  return useQuery({
    queryKey: clavesCartera.estadoDeCuenta(id ?? '', desde, hasta),
    queryFn: ({ signal }) =>
      datos.obtener<ReporteContable>(`${BASE}/clientes/${encodeURIComponent(id ?? '')}/estado-de-cuenta`, {
        consulta: { desde, hasta },
        senal: signal,
      }),
    enabled: id !== null && desde !== '' && hasta !== '',
  })
}

export function descargarEstadoDeCuenta(id: string, desde: string, hasta: string, formato: 'pdf' | 'xlsx' | 'csv'): Promise<void> {
  return descargarArchivo(`${BASE}/clientes/${encodeURIComponent(id)}/estado-de-cuenta`, { desde, hasta, formato })
}

/** Las facturas de un cliente, pendientes o todas. Sin cliente, la cartera entera. */
export function useFacturasCartera(cliente: string | null, todas = false) {
  return useQuery({
    queryKey: clavesCartera.facturas(cliente, todas),
    queryFn: ({ signal }) =>
      datos.obtener<FacturaCartera[]>(`${BASE}/facturas`, { consulta: { cliente: cliente ?? undefined, todas: todas || undefined }, senal: signal }),
    enabled: cliente !== null,
  })
}

/** El límite de crédito frente a lo que se va a fiar: aviso antes de emitir. */
export function useEvaluacionCredito(identificacion: string, monto: string) {
  return useQuery({
    queryKey: clavesCartera.credito(identificacion, monto),
    queryFn: ({ signal }) =>
      datos.obtener<EvaluacionCredito>(`${BASE}/credito`, { consulta: { identificacion, monto }, senal: signal }),
    enabled: identificacion.trim() !== '' && monto !== '' && monto !== '0.00',
    staleTime: 30_000,
  })
}

// ---------------------------------------------------------------------------
// Recibos, anticipos y aplicaciones
// ---------------------------------------------------------------------------

export function useRecibos(filtro: FiltroRecibos) {
  return useQuery({
    queryKey: clavesCartera.recibos(filtro),
    queryFn: ({ signal }) => datos.obtener<ReciboCartera[]>(`${BASE}/recibos`, { consulta: { ...filtro }, senal: signal }),
    placeholderData: (anterior) => anterior,
  })
}

export function descargarRecibos(filtro: FiltroRecibos, formato: 'pdf' | 'xlsx' | 'csv'): Promise<void> {
  return descargarArchivo(`${BASE}/recibos`, { ...filtro, formato })
}

export function useRecibo(id: string | null) {
  return useQuery({
    queryKey: clavesCartera.recibo(id ?? ''),
    queryFn: ({ signal }) => datos.obtener<ReciboDetalle>(`${BASE}/recibos/${encodeURIComponent(id ?? '')}`, { senal: signal }),
    enabled: id !== null,
  })
}

export function descargarReciboPdf(id: string): Promise<void> {
  return descargarArchivo(`${BASE}/recibos/${encodeURIComponent(id)}/pdf`)
}

/**
 * Registrar un recibo de ingreso. La clave la da el formulario: es el recibo.
 * Con la misma clave el servidor devuelve el mismo recibo (`repetido`), no otro
 * número.
 */
export function useRegistrarRecibo(claveIdempotencia: string) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (v: PeticionRecibo) =>
      datos.crear<ReciboRegistrado>(`${BASE}/recibos`, { cuerpo: v, claveIdempotencia, reintentos: 2 }),
    onSuccess: () => invalidar(cliente),
  })
}

export function useAnularRecibo() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      datos.crear<{ aplicacionesAnuladas: number }>(`${BASE}/recibos/${encodeURIComponent(id)}/anular`, { cuerpo: { motivo } }),
    onSuccess: () => invalidar(cliente),
  })
}

export function useSaldosAFavor(cliente: string | null) {
  return useQuery({
    queryKey: clavesCartera.saldosAFavor(cliente),
    queryFn: ({ signal }) => datos.obtener<SaldosAFavor>(`${BASE}/saldos-a-favor`, { consulta: { cliente: cliente ?? undefined }, senal: signal }),
  })
}

export function useAplicarSaldo(claveIdempotencia: string) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (v: PeticionAplicarSaldo) =>
      datos.crear<{ id: AplicacionCartera['id'] }>(`${BASE}/aplicaciones`, { cuerpo: v, claveIdempotencia, reintentos: 2 }),
    onSuccess: () => invalidar(cliente),
  })
}

export function useAnularAplicacion() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      datos.crear<void>(`${BASE}/aplicaciones/${encodeURIComponent(id)}/anular`, { cuerpo: { motivo } }),
    onSuccess: () => invalidar(cliente),
  })
}

// ---------------------------------------------------------------------------
// Recordatorios y WhatsApp
// ---------------------------------------------------------------------------

export function useRecordatorios(estado: EstadoRecordatorio | null) {
  return useQuery({
    queryKey: clavesCartera.recordatorios(estado),
    queryFn: ({ signal }) => datos.obtener<Recordatorio[]>(`${BASE}/recordatorios`, { consulta: { estado: estado ?? undefined }, senal: signal }),
  })
}

export function usePlantillasRecordatorio() {
  return useQuery({
    queryKey: clavesCartera.plantillas,
    queryFn: ({ signal }) => datos.obtener<PlantillasRecordatorio>(`${BASE}/recordatorios/plantillas`, { senal: signal }),
  })
}

export function useGuardarPlantillaRecordatorio() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (v: PeticionPlantillaRecordatorio) => datos.reemplazar<unknown>(`${BASE}/recordatorios/plantillas`, { cuerpo: v }),
    onSuccess: () => invalidar(cliente),
  })
}

export function useCrearPlantillasSugeridas() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: () => datos.crear<{ creadas: number }>(`${BASE}/recordatorios/plantillas/sugeridas`),
    onSuccess: () => invalidar(cliente),
  })
}

/** Marca como abierto un recordatorio de WhatsApp y devuelve el enlace wa.me para abrirlo. */
export function useAbrirRecordatorio() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => datos.crear<{ whatsApp: string | null }>(`${BASE}/recordatorios/${encodeURIComponent(id)}/abrir`),
    onSuccess: () => invalidar(cliente),
  })
}

export function useDescartarRecordatorio() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => datos.crear<Recordatorio>(`${BASE}/recordatorios/${encodeURIComponent(id)}/descartar`),
    onSuccess: () => invalidar(cliente),
  })
}

export function usePedirRecordatorio() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ cliente: id, ...v }: PeticionRecordatorio & { cliente: string }) =>
      datos.crear<RecordatorioPedido>(`${BASE}/clientes/${encodeURIComponent(id)}/recordatorio`, { cuerpo: v }),
    onSuccess: () => invalidar(cliente),
  })
}

/**
 * Prepara el mensaje de WhatsApp de un comprobante: el enlace público a su
 * representación impresa y el wa.me con el texto. **No manda nada**: el chat lo
 * abre una persona desde el teléfono del negocio. Queda anotado en Entregas.
 */
export function useWhatsAppDocumento() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ documento, ...v }: { documento: string; telefono?: string; conEnlace?: boolean; texto?: string }) =>
      datos.crear<WhatsAppDocumento>(`${BASE}/documentos/${encodeURIComponent(documento)}/whatsapp`, { cuerpo: v }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['entregas'] }),
  })
}

export function useRevocarEnlaces() {
  return useMutation({
    mutationFn: (documento: string) =>
      datos.crear<{ revocados: number }>(`${BASE}/documentos/${encodeURIComponent(documento)}/enlaces/revocar`),
  })
}

export function descargarPdfDocumento(documento: string): Promise<void> {
  return descargarArchivo(`/documentos/${encodeURIComponent(documento)}/pdf`)
}

/** Los hallazgos de un 422 de validación previa, si el error los trae. */
export function hallazgosDe(error: unknown): HallazgoValidacion[] {
  if (!(error instanceof ErrorApi) || error.crudo === undefined) return []
  try {
    const cuerpo = JSON.parse(error.crudo) as { hallazgos?: unknown }
    return Array.isArray(cuerpo.hallazgos) ? (cuerpo.hallazgos as HallazgoValidacion[]) : []
  } catch {
    return []
  }
}
